import { useState, useRef, forwardRef, HTMLAttributes } from 'react'
import { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd'
import { MetricWithEntries, DateColumn } from '../../types'
import UserAvatar from '../shared/UserAvatar'
import FloatingMenu from '../shared/FloatingMenu'
import { formatMetricValue } from './formatters'
import { MetricValueInput, WonLostToggle } from './cellControls'
import InlineTargetCell from './InlineTargetCell'

export const scoreCellKey = (metricId: number, columnKey: string) => `${metricId}:${columnKey}`

// The $/% affordance the shared cell input renders while focused. Team
// metrics carry a `format`; fall back to the free-text unit.
function unitFor(metric: MetricWithEntries): string {
  if (metric.format === 'currency') return '$'
  if (metric.format === 'percentage') return '%'
  return metric.unit || '#'
}

interface Props {
  metric: MetricWithEntries
  dateColumns: DateColumn[]
  teamId: number
  savingCells: Set<string>
  /** True while this row's inline target edit is in flight. */
  savingTarget?: boolean
  /** Commits an inline target edit; `raw` is '' when the field was cleared. */
  onTargetSave: (metric: MetricWithEntries, raw: string) => void
  onValueChange: (metric: MetricWithEntries, column: DateColumn, rawValue: string) => void
  onValueEnter: (metricId: number, columnKey: string, el: HTMLInputElement) => void
  onValueBlur: (metricId: number, columnKey: string) => void
  registerValueRef: (metricId: number, columnKey: string, el: HTMLInputElement | null) => void
  onWonLostToggle: (metric: MetricWithEntries, column: DateColumn, newValue: boolean) => void
  onEditNotes: (metric: MetricWithEntries) => void
  onEdit: (metric: MetricWithEntries) => void
  onArchive: (metric: MetricWithEntries) => void
  onDelete: (metric: MetricWithEntries) => void
  viewMode?: 'numbers' | 'wonlost' | 'both'
  dragHandleProps?: DraggableProvidedDragHandleProps | null
  isDragging?: boolean
  rowProps?: HTMLAttributes<HTMLTableRowElement>
}

const MetricRow = forwardRef<HTMLTableRowElement, Props>(function MetricRow(
  {
    metric,
    dateColumns,
    teamId: _teamId,
    savingCells,
    savingTarget = false,
    onTargetSave,
    onValueChange,
    onValueEnter,
    onValueBlur,
    registerValueRef,
    onWonLostToggle,
    onEditNotes,
    onEdit,
    onArchive,
    onDelete,
    viewMode = 'both',
    dragHandleProps,
    isDragging,
    rowProps,
  },
  fwdRef
) {
  const [menuOpen, setMenuOpen] = useState(false)
  const menuTriggerRef = useRef<HTMLButtonElement>(null)

  const handleArchive = () => {
    setMenuOpen(false)
    onArchive(metric)
  }

  const handleDelete = () => {
    setMenuOpen(false)
    onDelete(metric)
  }

  const handleEdit = () => {
    setMenuOpen(false)
    onEdit(metric)
  }

  const handleEditNotes = () => {
    setMenuOpen(false)
    onEditNotes(metric)
  }

  const rowBg = isDragging ? 'bg-[#F5F5F5]' : 'bg-white group-hover:bg-[#F5F5F5]/20'
  const { className: extraClass, ...restRowProps } = rowProps || {}
  return (
    <tr
      ref={fwdRef}
      {...restRowProps}
      className={`border-b border-stone/10 last:border-0 hover:bg-[#F5F5F5]/20 transition-colors group ${isDragging ? 'shadow-lg' : ''} ${extraClass || ''}`}
    >
      <td className={`py-2.5 px-2 sticky left-0 ${rowBg} z-20 w-[36px] min-w-[36px] max-w-[36px]`}>
        <div
          {...(dragHandleProps || {})}
          className="flex items-center justify-center w-5 h-5 select-none"
          style={{ cursor: dragHandleProps ? 'grab' : 'default', touchAction: 'none' }}
          aria-label={dragHandleProps ? 'Drag to reorder' : undefined}
          role={dragHandleProps ? 'button' : undefined}
        >
          {/* Six-dot handle — the same glyph Rock Review and the in-meeting
              scoreboard use, so every drag affordance looks identical. */}
          <svg className="w-4 h-4 text-text-muted flex-shrink-0 pointer-events-none" fill="currentColor" viewBox="0 0 20 20">
            <path d="M7 4a1 1 0 110 2 1 1 0 010-2zm0 5a1 1 0 110 2 1 1 0 010-2zm0 5a1 1 0 110 2 1 1 0 010-2zm6-10a1 1 0 110 2 1 1 0 010-2zm0 5a1 1 0 110 2 1 1 0 010-2zm0 5a1 1 0 110 2 1 1 0 010-2z" />
          </svg>
        </div>
      </td>
      <td className={`py-2.5 px-3 sticky left-[36px] ${rowBg} z-20 w-[180px] min-w-[180px] max-w-[180px]`}>
        <button
          onClick={handleEdit}
          className="text-sm font-medium text-text-primary hover:text-pinnacle-red transition-colors text-left truncate max-w-[156px] block"
          title={metric.name}
        >
          {metric.name}
        </button>
        {metric.description && (
          <p className="text-sm text-text-secondary truncate max-w-[156px]">{metric.description}</p>
        )}
      </td>
      <td className={`py-2.5 px-3 sticky left-[216px] ${rowBg} z-20 w-[140px] min-w-[140px] max-w-[140px]`}>
        {metric.owner ? (
          <div className="flex items-center gap-2 min-w-0">
            <UserAvatar user={metric.owner} size="xs" />
            <span className="text-sm text-text-primary truncate">{metric.owner.full_name}</span>
          </div>
        ) : (
          <span className="text-sm text-text-muted">Unassigned</span>
        )}
      </td>
      <td className={`py-2.5 px-3 sticky left-[356px] ${rowBg} z-20 w-[140px] min-w-[140px] max-w-[140px] overflow-hidden`}>
        {/* Double-click edits the target in place (task #650); clicking the
            metric name still opens the full settings modal. */}
        <InlineTargetCell
          value={metric.target}
          display={metric.formatted_target}
          canEdit
          isSaving={savingTarget}
          onSave={raw => onTargetSave(metric, raw)}
          ariaLabel={`Target for ${metric.name}`}
          className="text-sm text-text-primary font-medium block truncate"
          title={metric.formatted_target}
          inputWidth={110}
        />
      </td>
      <td className={`py-2.5 px-3 sticky left-[496px] ${rowBg} z-20 w-[130px] min-w-[130px] max-w-[130px] overflow-hidden`} style={{ boxShadow: '4px 0 8px -2px rgba(0,0,0,0.08)' }}>
        <span className="text-sm text-text-primary block truncate" title={formatMetricValue(metric.ytd_total, metric.format, metric.decimal_places, '—', metric.abbreviate_large_numbers)}>
          {formatMetricValue(metric.ytd_total, metric.format, metric.decimal_places, '—', metric.abbreviate_large_numbers)}
        </span>
      </td>

      {dateColumns.map(col => {
        const entry = metric.entries[col.key]
        const hasValue = !!entry && entry.value != null
        // Won/Lost (threshold-based, incl. manual overrides) drives the cell
        // color; strict on-target only when no Won/Lost state exists.
        const winState = hasValue ? (entry.won_the_week ?? entry.on_track) : null

        let bgColor = ''
        if (winState === true) bgColor = 'bg-status-on-track/10'
        else if (winState === false) bgColor = 'bg-status-off-track/10'

        const currentHighlight = col.is_current ? 'bg-charcoal/5' : ''
        const key = scoreCellKey(metric.id, col.key)
        const cellSaving = savingCells.has(key)
        const hasNotes = !!entry && !!(entry.context || entry.notes)

        return (
          <td
            key={col.key}
            className={`py-2 px-3 text-center min-w-[110px] transition-colors ${bgColor || currentHighlight}`}
          >
            {/* Nested controls: a click here edits THIS cell only — it must
                not bubble to the row (Edit) or start a row drag. */}
            <div
              className="inline-flex flex-col items-center gap-1"
              onClick={e => e.stopPropagation()}
              onMouseDown={e => e.stopPropagation()}
            >
              {viewMode !== 'wonlost' && (
                <div className="inline-flex items-center gap-1">
                  <MetricValueInput
                    unit={unitFor(metric)}
                    value={entry?.value ?? null}
                    formattedValue={formatMetricValue(entry?.value ?? null, metric.format, metric.decimal_places, '—', metric.abbreviate_large_numbers)}
                    onTarget={winState}
                    isSaving={cellSaving}
                    ariaLabel={`${metric.name} — ${col.label}`}
                    onChange={raw => onValueChange(metric, col, raw)}
                    onEnter={el => onValueEnter(metric.id, col.key, el)}
                    onBlurred={() => onValueBlur(metric.id, col.key)}
                    inputRef={el => registerValueRef(metric.id, col.key, el)}
                  />
                  {hasNotes && (
                    <svg className="w-3 h-3 text-text-muted flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-label="Has notes" role="img">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 01-1.581.814L10 13.197l-4.419 3.617A1 1 0 014 16V4z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              )}
              {viewMode !== 'numbers' && (
                <WonLostToggle
                  value={entry?.won_the_week ?? null}
                  manual={entry?.won_the_week_manual === true}
                  onChange={newVal => onWonLostToggle(metric, col, newVal)}
                  isSaving={cellSaving}
                  compact
                />
              )}
            </div>
          </td>
        )
      })}

      <td className={`py-2.5 px-2 sticky right-0 ${rowBg} z-20 w-[40px] min-w-[40px] max-w-[40px]`} style={{ boxShadow: '-4px 0 8px -2px rgba(0,0,0,0.08)' }}>
        <div className="relative">
          <button
            ref={menuTriggerRef}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={`Actions for ${metric.name}`}
            className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-[#351E1D]/10 text-text-muted hover:text-text-primary transition-colors"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
            </svg>
          </button>
          <FloatingMenu
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
            anchorRef={menuTriggerRef}
            width={192}
            className="w-48 bg-white rounded-lg shadow-elevated border border-stone/20 py-1"
          >
            <button
              onClick={handleEdit}
              className="w-full text-left px-3 py-2 text-sm text-text-primary hover:bg-[#F5F5F5]/50 transition-colors"
            >
              Edit
            </button>
            <button
              onClick={handleEditNotes}
              className="w-full text-left px-3 py-2 text-sm text-text-primary hover:bg-[#F5F5F5]/50 transition-colors"
            >
              Notes &amp; Targets
            </button>
            <button
              onClick={handleArchive}
              className="w-full text-left px-3 py-2 text-sm text-text-primary hover:bg-[#F5F5F5]/50 transition-colors"
            >
              {metric.state === 'archived' ? 'Activate' : 'Archive'}
            </button>
            <button
              onClick={handleDelete}
              className="w-full text-left px-3 py-2 text-sm text-[#ED1C24] hover:bg-[rgba(237,28,36,0.15)] transition-colors"
            >
              Delete
            </button>
          </FloatingMenu>
        </div>
      </td>
    </tr>
  )
})

export default MetricRow
